import type React from 'react';
import { useMemo } from 'react';

import { PenaltyTypeEnum } from '@clubspark-node/clubspark-tennis-types';
import { Player } from '@clubspark-react/ui/core';
import { type DialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Field, Form, Formik } from 'formik';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import * as yup from 'yup';

import { localTimeZoneAtom, tournamentEndDateAtom, tournamentStartDateAtom } from 'atoms/tournament';
import StandardButton from 'components/buttons/StandardButton';
import { useStyles } from 'components/dialogs/penaltyDialog/styles';
import TMXDialog from 'components/dialogs/TMXDialog';
import TMXInput from 'components/inputs/TMXInput';
import { getStartAndAvailableDates } from 'containers/schedule/utils';
import { useTournamentContext } from 'hooks/tournament/useTournamentContext';
import { type ParticipantInterface, PenaltyExtensionNames, type PenaltyInterface } from 'typedefs/store/tmxTypes';
import { factorySafeTimeToDayjs } from 'utils/dateTimeUtils';
import { participantName } from 'utils/participant';

import FormGroup from './components/FormGroup';
import FormLabel from './components/FormLabel';
import SuspensionPointsSelect from './components/SuspensionPointsSelect';
import TournamentDatesSelect from './components/TournamentDatesSelect';

interface PenaltyPointsDialogProps extends DialogProps {
  onSave?: (penaltyData: PenaltyInterface) => void;
  participantPoint?: Partial<PenaltyInterface> | null;
  participant?: ParticipantInterface | null;
}

interface PointFormValues {
  code: string;
  notes: string;
  issuedAt: string;
}

const PenaltyPointsDialog: React.FC<React.PropsWithChildren<PenaltyPointsDialogProps>> = ({
  onSave,
  participantPoint,
  participant,
  ...props
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const tournamentContext = useTournamentContext();
  const startDate = useAtomValue(tournamentStartDateAtom);
  const endDate = useAtomValue(tournamentEndDateAtom);
  const localTimeZone = useAtomValue(localTimeZoneAtom);

  const dates = useMemo(
    () =>
      getStartAndAvailableDates(startDate?.toISOString(), endDate?.toISOString(), localTimeZone)?.availableDates?.map(
        ({ value }) => value,
      ),
    [endDate, startDate, localTimeZone],
  );

  const defaultIssuedAt = useMemo(
    () =>
      dates.find(
        (date) => date === factorySafeTimeToDayjs(participantPoint?.issuedAt, localTimeZone)?.format('YYYY-MM-DD'),
      ),
    [dates, participantPoint?.issuedAt, localTimeZone],
  );

  const initialValues: PointFormValues = {
    code: participantPoint?.penaltyCode || '',
    notes: participantPoint?.notes || '',
    issuedAt: defaultIssuedAt ?? dates?.[0],
  };

  const validationSchema = yup.object().shape({
    code: yup.string().required(),
    issuedAt: yup.string().required(),
  });

  const handleSubmit = (values: PointFormValues) => {
    const suspensionPoint = tournamentContext.suspensionPoints.find((sp) => sp.code === values.code);

    const newParticipantPoint: PenaltyInterface = {
      ...participantPoint,
      penaltyId: participantPoint?.penaltyId || uuid(),
      createdAt: participantPoint.createdAt || new Date().toISOString(),
      penaltyCode: values.code,
      penaltyType: PenaltyTypeEnum.OTHER,
      issuedAt: values.issuedAt,
      notes: values.notes,
      extensions: [
        {
          name: PenaltyExtensionNames.POINTS,
          value: suspensionPoint.points,
          description: t('suspensionCodePointIssue'),
        },
        {
          name: PenaltyExtensionNames.SHORT_DESCRIPTION,
          value: suspensionPoint.shortDescription,
          description: t('suspensionCodeShortIssue'),
        },
        {
          name: PenaltyExtensionNames.LONG_DESCRIPTION,
          value: suspensionPoint.longDescription,
          description: t('suspensionCodeDescriptionIssue'),
        },
      ],
    };

    if (typeof onSave === 'function') {
      onSave(newParticipantPoint);
    }
  };

  const onClose = (e) => {
    if (typeof props.onClose === 'function') {
      props.onClose(e, 'escapeKeyDown');
    }
  };

  return (
    <TMXDialog open={open} {...props} PaperProps={{ className: classes.pointsDialogPaper }}>
      <Formik validateOnMount initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ isValid, isSubmitting, setFieldValue }) => (
          <Form>
            <DialogContent className={classes.pointsDialogContent}>
              <Grid container direction="column">
                <Grid item>
                  <Typography className={cx(classes.dialogTitle, classes.pointsDialogTitle)} variant="h2">
                    {t('addPoints')}
                  </Typography>
                </Grid>
                <Grid item>
                  {Boolean(participant) && (
                    <FormGroup>
                      <FormLabel>{t('player')}</FormLabel>
                      <Player
                        index={1}
                        player={{ name: participantName(participant) }}
                        showAdditionalInfo={false}
                        sideIndex={1}
                      />
                    </FormGroup>
                  )}
                  <FormGroup>
                    <FormLabel htmlFor="code">{t('suspensionCode')}</FormLabel>
                    <Field
                      component={SuspensionPointsSelect}
                      id="code"
                      initialValue={initialValues.code}
                      name="code"
                      suspensionPoints={tournamentContext.suspensionPoints}
                      onChange={(event) => setFieldValue('code', event.target.value)}
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel htmlFor="issuedAt">{t('issuedDate')}</FormLabel>
                    <Field
                      component={TournamentDatesSelect}
                      dates={dates}
                      id="issuedAt"
                      initialValue={initialValues.issuedAt}
                      name="issuedAt"
                      onChange={(event) => setFieldValue('issuedAt', event.target.value)}
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>{t('comments')}</FormLabel>
                    <Field fullWidth multiline component={TMXInput} name="notes" rows={4} />
                  </FormGroup>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions className={classes.pointsDialogActions}>
              <div className={classes.dialogActionsContent}>
                <Grid container justifyContent="end" spacing={2}>
                  <Grid item>
                    <StandardButton onClick={onClose}>{t('Close')}</StandardButton>
                  </Grid>
                  <Grid item>
                    <StandardButton disabled={!isValid || isSubmitting} type="submit" variant="primary">
                      {t('save')}
                    </StandardButton>
                  </Grid>
                </Grid>
              </div>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </TMXDialog>
  );
};

export default PenaltyPointsDialog;
